import { useState } from "react";
import {
  ArrowDownCircleIcon,
  DocumentIcon,
  EllipsisHorizontalIcon,
  MusicalNoteIcon,
  PhotoIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { Button } from "V2.0/common/button";
import { formatTimestamp } from "..";
import { generateDownload } from "../Whatsapp/helpers/generate_download";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "V2.0/common/dropdown";

const mediaMessageRegEx = /(audio|image|video)::(.*)/;

function checkForMediaMessage(message) {
  const match = message.match(mediaMessageRegEx);

  if (match) {
    return {
      isMediaMessage: true,
      mediaType: match[1],
      mediaUrl: match[2],
      message: match[0],
    };
  }

  return {
    isMediaMessage: false,
  };
}

function AudioMessage({
  className,
  projectId,
  conversationId,
  mediaUrl,
  user,
  timestamp,
}) {
  const [loadMedia, setLoadMedia] = useState(false);

  const bubbleColor = user
    ? "bg-blue-700 text-gray-200"
    : "bg-blue-100 text-gray-700";

  return (
    <div className={className}>
      <div className="flex items-end">
        <div
          className={`w-50 max-w-sm md:max-w-md rounded-lg p-4 ${bubbleColor} `}
        >
          {loadMedia ? (
            <>
              <audio controls>
                <source src={mediaUrl} />
              </audio>
            </>
          ) : (
            <div className="flex items-center justify-between gap-x-4">
              <div className="flex items-center gap-x-2">
                <MusicalNoteIcon className="size-5" /> Audio
              </div>
              <Button
                outline
                className="rounded-lg"
                onClick={() => setLoadMedia(true)}
              >
                <ArrowDownCircleIcon className="size-8" />
              </Button>
            </div>
          )}
          {timestamp && (
            <div className="flex justify-end mt-2">
              <span className="text-xs text-gray-600">
                {formatTimestamp(timestamp)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Message({ className, title, onDownload, user, timestamp, loading }) {
  const bubbleColor = user
    ? "bg-blue-700 text-gray-200"
    : "bg-blue-100 text-gray-700";

  return (
    <div className={className}>
      <div className="flex items-end">
        <div
          className={`w-50 max-w-sm md:max-w-md rounded-lg ${bubbleColor} p-4`}
        >
          <div className="flex items-center justify-between gap-x-4">
            <div className="flex items-center gap-x-2">{title}</div>
            <Button
              outline
              className="rounded-lg"
              loading={loading}
              onClick={onDownload}
            >
              <ArrowDownCircleIcon className="size-8" />
            </Button>
          </div>
          {timestamp && (
            <div className="flex justify-end mt-2">
              <span className="text-xs text-gray-600">
                {formatTimestamp(timestamp)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VideoMessage({
  className,
  projectId,
  conversationId,
  mediaUrl,
  user,
  timestamp,
}) {
  return (
    <Message
      title={
        <>
          <VideoCameraIcon className="size-5" /> Video
        </>
      }
      user={user}
      onDownload={() => generateDownload(`video.mp4`, mediaUrl)}
      timestamp={timestamp}
      className={className}
      loading={false}
    />
  );
}

function ImageMessage({
  className,
  projectId,
  conversationId,
  mediaUrl,
  user,
  timestamp,
}) {
  const [loadMedia, setLoadMedia] = useState(false);
  const bubbleColor = user
    ? "bg-blue-700 text-gray-200"
    : "bg-blue-100 text-gray-700";

  return (
    <div className={className}>
      <div className="flex items-end">
        <div
          className={`w-50 max-w-sm md:max-w-md rounded-lg p-4 ${bubbleColor}`}
        >
          {loadMedia ? (
            <div className="w-64 h-64 overflow-hidden">
              <div className="flex justify-end">
                <Dropdown>
                  <DropdownButton plain>
                    <EllipsisHorizontalIcon />
                  </DropdownButton>
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => generateDownload(`image.jpeg`, mediaUrl)}
                    >
                      Download
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
              <img src={mediaUrl} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="flex items-center justify-between gap-x-4">
              <div className="flex items-center gap-x-2">
                <PhotoIcon className="size-5" /> Image
              </div>
              <Button
                outline
                className="rounded-lg"
                onClick={() => setLoadMedia(true)}
              >
                <ArrowDownCircleIcon className="size-8" />
              </Button>
            </div>
          )}
          {timestamp && (
            <div className="flex justify-end mt-2">
              <span className="text-xs text-gray-600">
                {formatTimestamp(timestamp)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const MEDIA_MESSAGE_TYPE = {
  AUDIO: "audio",
  IMAGE: "image",
  VIDEO: "video",
};

const MediaMessages = {
  [MEDIA_MESSAGE_TYPE.AUDIO]: (props) => <AudioMessage {...props} />,
  [MEDIA_MESSAGE_TYPE.VIDEO]: (props) => <VideoMessage {...props} />,
  [MEDIA_MESSAGE_TYPE.IMAGE]: (props) => <ImageMessage {...props} />,
};

export {
  MEDIA_MESSAGE_TYPE as INSTAGRAM_MEDIA_MESSAGE_TYPE,
  MediaMessages as InstagramMediaMessages,
  checkForMediaMessage as checkForInstagramMediaMessage,
};
